import { useEffect } from "react";
import { usePostHog } from "@posthog/react";

import { readClerkIdentity, readClerkUserId } from "../lib/pro-identity";

/**
 * Associates PostHog events with the signed-in agent.
 *
 * Mirrors the consumer app's PostHogIdentity but reads the Clerk identity off
 * the `window.Clerk` global (the provider-agnostic pattern used across the Pro
 * surface) rather than the `useAuth`/`useUser` hooks — those require a mounted
 * <ClerkProvider>, which preview deliberately omits. Mounted only inside the
 * PostHogProvider (no key → no provider → nothing to identify), so `usePostHog`
 * always resolves a real client here.
 *
 * The Clerk user resource may not be hydrated on first render (a hard refresh
 * renders before sign-in resolves), so we identify once now AND subscribe to
 * Clerk's listener to catch the moment it hydrates. distinct_id is the Clerk
 * user id, matching the consumer app + the backend agent-funnel events.
 */
export function PostHogIdentity() {
  const posthog = usePostHog();

  useEffect(() => {
    if (!posthog) return;

    let done = false;
    const identifyOnce = () => {
      if (done) return;
      const id = readClerkUserId();
      if (!id) return;
      done = true;
      const { email } = readClerkIdentity();
      posthog.identify(id, {
        surface: "pro",
        auth_provider: "clerk",
        ...(email ? { email } : {}),
      });
      // First app open of this session, tied to the identified agent.
      posthog.capture("agent.app_opened", { surface: "pro" });
      captureAgentReturned(posthog, id);
    };

    identifyOnce();

    const clerk = (
      globalThis as { Clerk?: { addListener?: (cb: () => void) => () => void } }
    ).Clerk;
    const unsubscribe = clerk?.addListener?.(identifyOnce);
    return () => {
      unsubscribe?.();
    };
  }, [posthog]);

  return null;
}

interface ReturnTrackerPostHog {
  capture: (event: string, props?: Record<string, unknown>) => void;
}

interface AgentReturnState {
  first_opened_on: string;
  last_returned_on?: string;
}

function captureAgentReturned(posthog: ReturnTrackerPostHog, clerkUserId: string) {
  if (typeof window === "undefined" || !clerkUserId) return;
  const today = localDateString(new Date());
  const key = `sill.pro.agent_return.${clerkUserId}`;
  const state = readReturnState(key);
  if (!state?.first_opened_on) {
    writeReturnState(key, { first_opened_on: today });
    return;
  }
  if (state.first_opened_on < today && state.last_returned_on !== today) {
    posthog.capture("agent.returned", {
      surface: "pro",
      return_basis: "later_calendar_day",
    });
    writeReturnState(key, {
      first_opened_on: state.first_opened_on,
      last_returned_on: today,
    });
  }
}

function readReturnState(key: string): AgentReturnState | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AgentReturnState>;
    if (typeof parsed.first_opened_on !== "string") return null;
    return {
      first_opened_on: parsed.first_opened_on,
      ...(typeof parsed.last_returned_on === "string"
        ? { last_returned_on: parsed.last_returned_on }
        : {}),
    };
  } catch {
    return null;
  }
}

function writeReturnState(key: string, state: AgentReturnState) {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Storage may be unavailable in privacy modes; analytics must stay best-effort.
  }
}

function localDateString(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}
